import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Risorsa } from '../interfaces/risorse.interface';
import { FiltroRisorse } from '../interfaces/filtro-risorse.interface';
import { Attori } from '../shared/enum/attori.enum';
import { FiltroResponsabili } from '../interfaces/filtro-responsabili.interface';

@Injectable({
  providedIn: 'root',
})
export class RisorseService {
  constructor(private readonly http: HttpClient) {}

  private _risorsa: Risorsa = {};
  set risorsa(value: Risorsa) {
    this._risorsa = value;
  }
  get risorsa(): Risorsa {
    return this._risorsa;
  }

  baseUrl = 'api/Timesheet';

  public getRisorseByAttore(
    attore: number,
    filter: FiltroRisorse
  ): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    switch (attore) {
      case Attori.ResponsabileCommessaDestinazione:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/responsabileCommessa/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
      case Attori.ResponsabilePrimoLivello:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/responsabilePrimoLivello/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
      case Attori.PianificazioneControllo:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/pianificazioneControllo/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
      case Attori.ControllerCentrale:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/controllerCentrale/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
      case Attori.ControllerBU:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/controllerBU/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
      case Attori.COO:
      case Attori.CFO:
      case Attori.DirettoreGenerale:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/coo/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
      default:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/responsabile/ricerca',
          filter,
          { responseType: responseType as 'json' }
        );
    }
  }
  public getRisorseCountByAttore(
    attore: number,
    filter: FiltroRisorse
  ): Observable<any> {
    switch (attore) {
      case Attori.ResponsabileCommessaDestinazione:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/responsabileCommessa/ricerca/count',
          filter
        );
      case Attori.ResponsabilePrimoLivello:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/responsabilePrimoLivello/ricerca/count',
          filter
        );
      case Attori.PianificazioneControllo:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/pianificazioneControllo/ricerca/count',
          filter
        );
      case Attori.ControllerCentrale:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/controllerCentrale/ricerca/count',
          filter
        );
      case Attori.ControllerBU:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/controllerBU/ricerca/count',
          filter
        );
      case Attori.COO:
      case Attori.CFO:
      case Attori.DirettoreGenerale:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/coo/ricerca/count',
          filter
        );
      default:
        return this.http.post<FiltroRisorse>(
          this.baseUrl + '/responsabile/ricerca/count',
          filter
        );
    }
  }

  getResponsabiliByAttore(attore: Attori, filter: FiltroResponsabili): Observable<any> {
    switch (attore) {
      case Attori.COO:
      case Attori.CFO:
      case Attori.DirettoreGenerale:
        return this.elencoResponsabiliPrimoLivello(filter);
      case Attori.PianificazioneControllo:
        if (filter.richiestaPerAttore === Attori.ResponsabileGerarchico) {
          return this.elencoResponsabiliPianificazioneControllo(filter);
        } else {
          return this.elencoResponsabiliCommessaPianificazioneControllo(filter);
        }

      default:
        return of(null);
    }
  }

  getResponsabiliCountByAttore(attore: Attori, filter: FiltroResponsabili): Observable<any> {
    switch (attore) {
      case Attori.COO:
      case Attori.CFO:
      case Attori.DirettoreGenerale:
        return this.elencoResponsabiliPrimoLivelloCount(filter);
      case Attori.PianificazioneControllo:
        if (filter.richiestaPerAttore === Attori.ResponsabileGerarchico) {
          return this.elencoResponsabiliPianificazioneControlloCount(filter);
        } else {
          return this.elencoResponsabiliCommessaPianificazioneControlloCount(filter);
        }

      default:
        return of(null);
    }
  }

  elencoResponsabiliPianificazioneControllo(filter: FiltroResponsabili): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    return this.http.post<any>(this.baseUrl + '/pianificazioneControllo/responsabili/ricerca', filter, {
      responseType: responseType as 'json',
    });
  }

  elencoResponsabiliPianificazioneControlloCount(filter: FiltroResponsabili): Observable<any> {
    return this.http.post(this.baseUrl + '/pianificazioneControllo/responsabili/ricerca/count', filter);
  }

  elencoResponsabiliCommessaPianificazioneControllo(filter: FiltroResponsabili): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    return this.http.post<any>(this.baseUrl + '/pianificazioneControllo/responsabilicommessa/ricerca', filter, {
      responseType: responseType as 'json',
    });
  }

  elencoResponsabiliCommessaPianificazioneControlloCount(filter: FiltroResponsabili): Observable<any> {
    return this.http.post(this.baseUrl + '/pianificazioneControllo/responsabilicommessa/ricerca/count', filter);
  }

  elencoResponsabiliPrimoLivello(filter: FiltroResponsabili): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    return this.http.post<any>(this.baseUrl + '/coo/responsabiliPrimoLivello/ricerca', filter, {
      responseType: responseType as 'json',
    });
  }

  elencoResponsabiliPrimoLivelloCount(filter: FiltroResponsabili): Observable<any> {
    return this.http.post(this.baseUrl + '/coo/responsabiliPrimoLivello/ricerca/count', filter);
  }

  elencoResponsabili(filter: FiltroResponsabili): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    return this.http.post<any>(this.baseUrl + '/coo/responsabili/ricerca', filter, {
      responseType: responseType as 'json',
    });
  }

  elencoResponsabiliCount(filter: FiltroResponsabili): Observable<any> {
    return this.http.post(this.baseUrl + '/coo/responsabili/ricerca/count', filter);
  }

  elencoRisorseRg(filter: FiltroRisorse): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    return this.http.post<any>(this.baseUrl + '/responsabile/ricerca', filter, {
      responseType: responseType as 'json',
    });
  }

  elencoRisorseRPL(filter: FiltroRisorse): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    return this.http.post<any>(
      this.baseUrl + '/responsabilePrimoLivello/ricerca',
      filter,
      { responseType: responseType as 'json' }
    );
  }

  elencoRisorseRgCount(filter: any): Observable<any> {
    return this.http.post(this.baseUrl + '/responsabile/ricerca/count', filter);
  }

  patchMancataPresaVisione(motivo: any): Observable<any> {
    return this.http.patch(this.baseUrl + '/mancataPresaVisione', motivo);
  }
}
